/**
 * LifeScribe Admin Dashboard - Design Tokens
 * Central repository for all design values (colors, typography, spacing, etc.)
 * Based on LifeScribe Design System v4.0
 */

// ===== COLOR SYSTEM =====

export const colors = {
  // Primary Brand Colors (50-900 scale)
  deepTeal: {
    50: '#E6F2F5',
    100: '#CCE5EA',
    200: '#99CBD5',
    300: '#66B1C0',
    400: '#3397AB',
    500: '#1A5F7A',  // Main brand color
    600: '#154D62',
    700: '#103A49',
    800: '#0B2731',
    900: '#051318',
  },

  warmGold: {
    50: '#FFF9E6',
    100: '#FFF3CC',
    200: '#FFE799',
    300: '#FFDB66',
    400: '#FFCF33',
    500: '#D4AC0D',  // Main accent color
    600: '#A8890A',
    700: '#7C6608',
    800: '#504405',
    900: '#242103',
  },

  softCream: {
    50: '#FEFDFB',
    100: '#FCF9F3',
    200: '#F9F3E9',  // Main background color
    300: '#F5EDD9',
    400: '#F0E7C9',
    500: '#EBE1B9',
    600: '#E6DBA9',
    700: '#E1D599',
    800: '#DCCF89',
    900: '#D7C979',
  },

  slateGray: {
    50: '#F8F9FA',
    100: '#E9ECEF',
    200: '#DEE2E6',
    300: '#CED4DA',
    400: '#ADB5BD',
    500: '#6C757D',
    600: '#5D6D7E',  // Main secondary text color
    700: '#495057',
    800: '#343A40',
    900: '#212529',
  },

  // Semantic Colors (Screen-Specific)
  revenue: {
    main: '#146152',    // Forest Green
    light: '#1A7A68',
    dark: '#0F4A3D',
    50: '#E6F2F0',
    100: '#CCE5E0',
    500: '#146152',
    900: '#0A312A',
  },

  users: {
    main: '#1A5F7A',    // Deep Teal (reuses primary)
    light: '#2A7F9A',
    dark: '#144B5F',
    50: '#E6F2F5',
    100: '#CCE5EA',
    500: '#1A5F7A',
    900: '#051318',
  },

  content: {
    main: '#D4AC0D',    // Warm Gold (reuses accent)
    light: '#E0BD2F',
    dark: '#A8890A',
    50: '#FFF9E6',
    100: '#FFF3CC',
    500: '#D4AC0D',
    900: '#242103',
  },

  costs: {
    main: '#7D1935',    // Rich Burgundy
    light: '#9E2247',
    dark: '#5E1228',
    50: '#F9E6EB',
    100: '#F3CCD6',
    500: '#7D1935',
    900: '#3F0C1A',
  },

  // Status Colors
  success: {
    main: '#146152',    // Forest Green
    light: '#1A7A68',
    dark: '#0F4A3D',
    50: '#E6F2F0',
    100: '#CCE5E0',
    500: '#146152',
    900: '#0A312A',
  },

  warning: {
    main: '#D4AC0D',    // Warm Gold
    light: '#E0BD2F',
    dark: '#A8890A',
    50: '#FFF9E6',
    100: '#FFF3CC',
    500: '#D4AC0D',
    900: '#242103',
  },

  error: {
    main: '#7D1935',    // Rich Burgundy
    light: '#9E2247',
    dark: '#5E1228',
    50: '#F9E6EB',
    100: '#F3CCD6',
    500: '#7D1935',
    900: '#3F0C1A',
  },

  info: {
    main: '#1A5F7A',    // Deep Teal
    light: '#2A7F9A',
    dark: '#144B5F',
    50: '#E6F2F5',
    100: '#CCE5EA',
    500: '#1A5F7A',
    900: '#051318',
  },

  // Neutral Grays (Universal)
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },

  // White & Black
  white: '#FFFFFF',
  black: '#000000',
};

// ===== TYPOGRAPHY SYSTEM =====

export const typography = {
  fonts: {
    display: '"Playfair Display", serif',      // For h1, h2 - elegant, premium
    heading: '"Montserrat", sans-serif',       // For h3-h6, buttons - modern, clean
    body: '"Source Sans Pro", sans-serif',     // For body text - readable, friendly
    mono: '"Fira Code", monospace',            // For code blocks - developer-focused
  },

  sizes: {
    xs: '0.75rem',      // 12px
    sm: '0.875rem',     // 14px
    base: '1rem',       // 16px
    lg: '1.125rem',     // 18px
    xl: '1.25rem',      // 20px
    '2xl': '1.5rem',    // 24px
    '3xl': '1.875rem',  // 30px
    '4xl': '2.25rem',   // 36px
    '5xl': '3rem',      // 48px
    '6xl': '3.75rem',   // 60px
    '7xl': '4.5rem',    // 72px
  },

  weights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
  },

  lineHeights: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
    loose: 2,
  },

  letterSpacing: {
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
};

// ===== SPACING SCALE =====

export const spacing = {
  0: '0',
  1: '0.25rem',   // 4px
  2: '0.5rem',    // 8px
  3: '0.75rem',   // 12px
  4: '1rem',      // 16px
  5: '1.25rem',   // 20px
  6: '1.5rem',    // 24px
  7: '1.75rem',   // 28px
  8: '2rem',      // 32px
  9: '2.25rem',   // 36px
  10: '2.5rem',   // 40px
  11: '2.75rem',  // 44px
  12: '3rem',     // 48px
  14: '3.5rem',   // 56px
  16: '4rem',     // 64px
  20: '5rem',     // 80px
  24: '6rem',     // 96px
  32: '8rem',     // 128px
  40: '10rem',    // 160px
  48: '12rem',    // 192px
  56: '14rem',    // 224px
  64: '16rem',    // 256px
};

// ===== SHADOWS =====

export const shadows = {
  none: 'none',
  xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
};

// ===== BORDERS =====

export const borders = {
  radius: {
    none: '0',
    sm: '0.25rem',    // 4px
    md: '0.5rem',     // 8px
    lg: '0.75rem',    // 12px
    xl: '1rem',       // 16px
    '2xl': '1.5rem',  // 24px
    '3xl': '2rem',    // 32px
    full: '9999px',   // Fully rounded
  },

  width: {
    none: '0',
    thin: '1px',
    medium: '2px',
    thick: '4px',
  },

  style: {
    solid: 'solid',
    dashed: 'dashed',
    dotted: 'dotted',
  },
};

// ===== Z-INDEX SCALE =====

export const zIndex = {
  base: 0,
  dropdown: 1000,
  sticky: 1020,
  fixed: 1030,
  backdrop: 1040,
  modal: 1050,
  popover: 1060,
  tooltip: 1070,
};

// ===== TRANSITIONS =====

export const transitions = {
  duration: {
    fastest: '75ms',
    faster: '100ms',
    fast: '150ms',
    normal: '200ms',
    slow: '300ms',
    slower: '500ms',
    slowest: '700ms',
  },

  easing: {
    linear: 'linear',
    in: 'cubic-bezier(0.4, 0, 1, 1)',
    out: 'cubic-bezier(0, 0, 0.2, 1)',
    inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  },
};

// ===== BREAKPOINTS =====

export const breakpoints = {
  xs: 0,      // Mobile portrait
  sm: 600,    // Mobile landscape
  md: 900,    // Tablet
  lg: 1200,   // Desktop
  xl: 1536,   // Large desktop
};

// ===== SCREEN-SPECIFIC COLOR MAPPINGS =====

export const screenColors = {
  dashboard: colors.deepTeal,
  users: colors.users,
  revenue: colors.revenue,
  subscriptions: colors.revenue,
  content: colors.content,
  ai: colors.content,
  communications: colors.deepTeal,
  family: colors.deepTeal,
  monitoring: colors.slateGray,
  cost: colors.costs,
  system: colors.slateGray,
};

// Type exports for TypeScript
export type ColorScale = typeof colors.deepTeal;
export type ColorName = keyof typeof colors;
export type TypographyFont = keyof typeof typography.fonts;
export type TypographySize = keyof typeof typography.sizes;
export type SpacingValue = keyof typeof spacing;
export type ShadowValue = keyof typeof shadows;
export type BorderRadius = keyof typeof borders.radius;
